import { AxiosResponse } from "axios";
import * as API from "middleware/api";
import {
  postCancelType,
  DevolutionUndeliveredResponseType,
  postDevolutionUndeliveredType,
  postDnideliveredRequestType,
  TransactionCancelResponseType,
  DetailTransactionResponseType,
  RelationshipResponse,
  DeliveryProofFileResponseType,
} from "sagas/types/detailTransactions";
import {
  TransactionsExportContentType,
  FilterTransactionsType,
  TransactionResponseContent,
  BulkCancelTransactionType,
} from "sagas/types/transactions";

export const getTransactions = (
  params: FilterTransactionsType
): Promise<AxiosResponse<TransactionResponseContent>> =>
  API.get("/ms-admin-rest/api/v1.0/transactions", params);
export const getTransactionsExport = (
  params: FilterTransactionsType
): Promise<AxiosResponse<TransactionsExportContentType>> => {
  const { date, offset, limit, ...body } = params;
  return API.get("/ms-admin-rest/api/v1.0/transactions.csv", body);
};

export const getDetailTransaction = (
  id: string
): Promise<AxiosResponse<DetailTransactionResponseType>> =>
  API.get(`/ms-admin-rest/api/v1.0/transactions/${id}`);
export const getMessages = (
  id: string
): Promise<AxiosResponse<TransactionCancelResponseType>> =>
  API.get(`ms-admin-rest/api/v1.0/transactions/${id}/message`);
export const postDevolutionUndelivered = (
  params: postDevolutionUndeliveredType,
  id: string
): Promise<AxiosResponse<DevolutionUndeliveredResponseType>> =>
  API.post(`/ms-admin-rest/api/v1.0/transactions/${id}/in-devolution`, params);
export const postReasonsCanceled = (
  params: postCancelType,
  id: string
): Promise<AxiosResponse<DevolutionUndeliveredResponseType>> =>
  API.post(`/ms-admin-rest/api/v1.0/transactions/${id}/cancel`, params);
export const postFinishReturned = (
  id: string
): Promise<AxiosResponse<DevolutionUndeliveredResponseType>> =>
  API.post(`/ms-admin-rest/api/v1.0/transactions/${id}/returned`, {});
export const postFinishLost = (
  id: string
): Promise<AxiosResponse<DevolutionUndeliveredResponseType>> =>
  API.post(`/ms-admin-rest/api/v1.0/transactions/${id}/lost`, {});
export type DeliveredHeadersType = {
  "country-code"?: string;
  "x-latitude"?: string;
  "x-longitude"?: string;
  "x-user-id"?: string;
  "x-rol-id"?: string;
};

export const postDnidelivered = (
  params: postDnideliveredRequestType,
  id: string,
  config?: { headers?: DeliveredHeadersType }
): Promise<AxiosResponse<DevolutionUndeliveredResponseType>> =>
  API.post(
    `/ms-admin-rest/api/v1.0/transactions/${id}/delivered`,
    params,
    config
  );
export const postCancelTransaction = (
  transactions: BulkCancelTransactionType
): Promise<AxiosResponse<DevolutionUndeliveredResponseType>> =>
  API.post(`/ms-admin-rest/api/v1.0/transactions/bulk-cancel`, transactions);
export const getProof = (
  id: string
): Promise<AxiosResponse<API.ApiResponseResult<{ link: string }>>> =>
  API.get(`ms-admin-rest/api/v1.0/transactions/${id}/proof-of-delivery`);

export type ProofOfDeliveryApiItem = { type: string; value: string };

export type ProofOfDeliveryApiObject = {
  photo: string | null;
  sign: string | null;
  identificationNumber: string | null;
  latitude: number;
  longitude: number;
  name: string;
  lastName: string;
  relationship: string | null;
  finishByAdmin?: boolean;
  timestamp?: string;
};

export type ProofOfDeliveryApiResult = {
  latitude?: number;
  longitude?: number;
  finishByAdmin?: boolean;
  timestamp?: string;
  proofOfDelivery: ProofOfDeliveryApiItem[] | ProofOfDeliveryApiObject;
};
export const getProofOfDelivery = (
  id: string
): Promise<AxiosResponse<API.ApiResponseResult<ProofOfDeliveryApiResult>>> =>
  API.get(`/ms-admin-rest/api/v1.0/transactions/${id}/proof-of-delivery`);
export const postDeliveryProof = (params: {
  photo: FormData;
  id: number;
}): Promise<AxiosResponse<{ id: string }>> =>
  API.post(
    `/ms-admin-rest/api/v1.0/transactions/${params.id}/delivery-proof/photo`,
    params.photo
  );

export const postDeliveryProofSign = (params: {
  sign: FormData;
  id: number;
}): Promise<AxiosResponse<{ id: string }>> =>
  API.post(
    `/ms-admin-rest/api/v1.0/transactions/${params.id}/delivery-proof/sign`,
    params.sign
  );

export type ProofOfDeliveryFileHeaders = {
  "x-user-id": string;
  "x-rol-id": string;
  "x-type": string;
  "app-version"?: string;
};

const proofOfDeliveryFilePath = (id: number) =>
  process.env.REACT_APP_PROOF_DELIVERY_USE_BFF_PATH === "true"
    ? `/api/v1.0/transactions/${id}/proof-of-delivery/file`
    : `/ms-admin-rest/api/v1.0/transactions/${id}/proof-of-delivery/file`;

export const postDeliveryProofFile = (
  id: number,
  file: FormData,
  headers: ProofOfDeliveryFileHeaders
): Promise<AxiosResponse<DeliveryProofFileResponseType>> =>
  API.post(proofOfDeliveryFilePath(id), file, {
    headers: {
      ...headers,
      "app-version": headers["app-version"] ?? "2.6.0",
    },
  });

export const getRelationship = (): Promise<
  AxiosResponse<RelationshipResponse>
> => API.get(`/ms-admin-rest/api/v1.0/relationships`);
